/**
 * SeverityRating
 *
 * @remarks
 * Severity Rating is a textual representation of the numeric Base, Temporal and Environmental scores.
 * All scores can be mapped to one the values of this enum.
 *
 * @see Documentation available in the CVSS v3.1 specification
 */
export enum SeverityRating {
    None = 'None',
    Low = 'Low',
    Medium = 'Medium',
    High = 'High',
    Critical = 'Critical',
}

/**
 * Map a CVSS score to a {@link SeverityRating} value
 *
 * @remarks
 * If the score is not defined, a {@link TypeError} will be thrown or
 * if the score is withing the range of a score, a {@link RangeError} will be thrown.
 *
 * @param overallScore - the CVSS score to convert
 * @returns the risk rating of the CVSS score
 */
export function mapScoreToSeverityRating(
    overallScore: number | null,
): SeverityRating {
    // If the score is not defined, throw an error
    if (typeof overallScore === 'undefined') {
        throw new Error('A CVSS score must be either a number or null');
    }

    // A explicit void of a score should be considered as 'none'
    if (overallScore === null) {
        return SeverityRating.None;
    }

    // If the score is not a number, throw an error
    if (isNaN(overallScore)) {
        throw new TypeError('CVSS score must be a number');
    }

    // Apply the CVSS risk rating based on the score
    if (overallScore > 9) {
        return SeverityRating.Critical;
    } else if (overallScore > 7) {
        return SeverityRating.High;
    } else if (overallScore > 4) {
        return SeverityRating.Medium;
    } else if (overallScore > 0) {
        return SeverityRating.Low;
    }

    return SeverityRating.None;
}

/**
 * Map a {@link SeverityRating} to a human readable text
 */
export function mapSeverityRatingToReadableText(
    severityRating: SeverityRating,
): string {
    switch (severityRating) {
        case SeverityRating.None:
            return 'None';
        case SeverityRating.Low:
            return 'Low';
        case SeverityRating.Medium:
            return 'Medium';
        case SeverityRating.High:
            return 'High';
        case SeverityRating.Critical:
            return 'Critical';
        default:
            return severityRating;
    }
}

const VERSION_REGEX = /^CVSS:(?<major>\d)(?:\.(?<minor>\d))?(?<metrics>.*)?$/;

/**
 * Parse a CVSS string and return the version
 */
export function getCVSSVersion(cvss: string): string {
    const versionRegexRes = VERSION_REGEX.exec(cvss);

    if (!versionRegexRes?.groups?.major) {
        throw new Error('Could not determine CVSS version');
    }

    const major = versionRegexRes.groups.major;
    const minor = versionRegexRes.groups.minor
        ? `.${versionRegexRes.groups.minor}`
        : '';

    return `${major}${minor}`;
}

/**
 * Convert a CVSS vector string to a Map
 * @param vector - CVSS vector string
 * @returns Map representation of the vector
 */
export function convertVectorToMap(vector: string): Map<string, string> {
    const map = new Map<string, string>();
    const parts = vector.split('/');
    parts.forEach((part) => {
        const [key, value] = part.split(':');
        map.set(key, value);
    });
    return map;
}

/**
 * Convert a Map to a CVSS vector string
 * @param map - Map representation of the vector
 * @returns CVSS vector string
 */
export function convertMapToVector(map: Map<string, string>): string {
    return Array.from(map)
        .map(([key, value]) => `${key}:${value}`)
        .join('/');
}
